import { useQuery } from '@tanstack/react-query'
import { SETTINGS, type SettingsSection, type SettingsValue } from '@hcd/domain'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { settingsQuery, useSaveSection } from '@/lib/settings'
import { issuesToErrors, ReadOnlyNote, SaveBar } from './form-kit'

/**
 * Loads one settings section, keeps a local draft, validates it with the same
 * schema the server uses and shows a save bar when anything has changed.
 */
export function SectionEditor<K extends SettingsSection>({
  section,
  canEdit,
  children,
}: {
  section: K
  canEdit: boolean
  children: (ctx: {
    draft: SettingsValue<K>
    set: <F extends keyof SettingsValue<K>>(field: F, value: SettingsValue<K>[F]) => void
    errors: Record<string, string>
    disabled: boolean
  }) => ReactNode
}) {
  const { data, isPending, isError, error } = useQuery(settingsQuery)
  const saved = data?.sections[section]
  const [draft, setDraft] = useState<SettingsValue<K> | null>(null)
  const save = useSaveSection(section)

  useEffect(() => {
    if (saved) setDraft(saved.value as SettingsValue<K>)
  }, [saved])

  const result = useMemo(
    () => (draft ? SETTINGS[section].schema.safeParse(draft) : null),
    [draft, section],
  )
  const errors = result && !result.success ? issuesToErrors(result.error.issues) : {}
  const dirty = !!draft && !!saved && JSON.stringify(draft) !== JSON.stringify(saved.value)

  if (isError)
    return (
      <p role="alert" className="text-destructive">
        {error.message}
      </p>
    )
  if (isPending || !draft || !saved) return <Skeleton className="h-64 w-full rounded-xl" />

  return (
    <>
      {!canEdit && <ReadOnlyNote who="an owner or platform admin" />}
      {children({
        draft,
        set: (field, value) => setDraft((d) => (d ? { ...d, [field]: value } : d)),
        errors,
        disabled: !canEdit,
      })}
      <SaveBar
        dirty={dirty}
        saving={save.isPending}
        valid={!!result?.success}
        onDiscard={() => setDraft(saved.value as SettingsValue<K>)}
        onSave={() =>
          result?.success &&
          save.mutate({ value: result.data as SettingsValue<K>, expectedVersion: saved.version })
        }
      />
    </>
  )
}
